/**
 * Shared date formatting utility.
 *
 * All dates are formatted in en-AU locale.
 */

interface FormatDateOptions {
  /** Include year in the output (default: true) */
  includeYear?: boolean;
  /** Include hours and minutes in the output (default: false) */
  includeTime?: boolean;
  /** String returned when the input is null/undefined (default: "—") */
  fallback?: string;
}

/**
 * Format a date string for display.
 *
 * @example
 * formatDate("2025-06-01T10:30:00Z")               // "1 Jun 2025"
 * formatDate("2025-06-01T10:30:00Z", { includeTime: true }) // "1 Jun 2025, 08:30 pm"
 * formatDate(null)                                   // "—"
 * formatDate(null, { fallback: "Never" })            // "Never"
 */
export function formatDate(
  dateStr: string | null | undefined,
  options: FormatDateOptions = {},
): string {
  const { includeYear = true, includeTime = false, fallback = "—" } = options;

  if (!dateStr) return fallback;

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return fallback;

  const opts: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    ...(includeYear && { year: "numeric" }),
    ...(includeTime && { hour: "2-digit", minute: "2-digit" }),
  };

  return parsed.toLocaleDateString("en-AU", opts);
}
